import { AppCacheStatus, AppCacheData } from './types';
import { AppCacheActions, DataPayload, FailedPayload } from './actions';

const initialAppCacheState: AppCacheData<any> = {
  status: AppCacheStatus.Initial,
  ready: false,
  safeMessage: null,
  currentID: null,
};

export const createAppCacheReducer =
  <T>() =>
  <
    L extends string,
    F extends string,
    R extends string,
    D extends string,
    Fr extends string,
    S extends string,
    C extends string,
  >(
    loading: L,
    failed: F,
    refetching: R,
    hasData: D,
    fresh: Fr,
    swap: S,
    clear: C,
  ) =>
  (initialState?: { [storageId: string]: AppCacheData<T> }) =>
  (
    state: { [storageId: string]: AppCacheData<T> } = initialState ?? {},
    action: AppCacheActions<L, F, R, D, Fr, S, C, T>,
  ): { [storageId: string]: AppCacheData<T> } => {
    switch (action.type) {
      case loading: {
        const appCacheState: AppCacheData<T> = state[action.storageId] ?? initialAppCacheState;
        return {
          ...state,
          [action.storageId]: {
            ...appCacheState,
            status: AppCacheStatus.IsLoading,
            currentID: action.id,
          },
        };
      }
      case failed: {
        const failed = action as FailedPayload<F>;
        if (action.id !== state[action.storageId]?.currentID) return state;
        return {
          ...state,
          [action.storageId]: {
            ...state[action.storageId],
            status: AppCacheStatus.LoadFailed,
            safeMessage: failed.safeMessage,
            ready: false,
            currentID: action.id,
          },
        };
      }
      case refetching: {
        const refetching = action as DataPayload<R, T>;
        if (action.id !== state[action.storageId]?.currentID) return state;
        return {
          ...state,
          [refetching.storageId]: {
            ...state[refetching.storageId],
            status: AppCacheStatus.IsRefetching,
            ready: true,
            data: refetching.data as T,
            safeMessage: null,
            currentID: action.id,
          },
        };
      }
      case hasData: {
        const hasData = action as DataPayload<D, T>;
        const appCacheState: AppCacheData<T> = state[action.storageId] ?? initialAppCacheState;
        if (action.id !== state[action.storageId]?.currentID) return state;
        return {
          ...state,
          [action.storageId]: {
            ...appCacheState,
            status: AppCacheStatus.HasData,
            newData: hasData.data as T,
            safeMessage: null,
            currentID: action.id,
          },
        };
      }
      case fresh: {
        const fresh = action as DataPayload<D, T>;
        if (action.id !== state[action.storageId]?.currentID) return state;
        return {
          ...state,
          [fresh.storageId]: {
            ...state[fresh.storageId],
            status: AppCacheStatus.Fresh,
            ready: true,
            data: fresh.data as T,
            safeMessage: null,
            currentID: action.id,
          },
        };
      }
      case swap: {
        const appCacheState: AppCacheData<T> = state[action.storageId] ?? initialAppCacheState;
        return {
          ...state,
          [action.storageId]: {
            ...appCacheState,
            status: AppCacheStatus.Fresh,
            data: appCacheState.newData as T,
            newData: null,
            safeMessage: null,
            currentID: null,
          },
        };
      }
      case clear: {
        const { [action.storageId]: toRemove, ...rest } = state;
        return rest;
      }
      default:
        return state;
    }
  };
